import { useEffect, useState, useMemo } from "react";
import { API_URL } from "../config";
import { usePreApproveImages } from "../hooks/usePreApproveImages";
import { useSelection } from "../hooks/useSelection";
import FiltersLabelPrompt from "../components/filters/FiltersLabelPrompts";
import ImageTableView from "../components/ImageTableView";
import Pagination from "../components/Pagination";
import Notification from "../components/Notification";
import { supabase } from "../supabaseClient";

function PreApproveImages() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const [availableLabels, setAvailableLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  const { selected, toggle, toggleAll, clear } = useSelection();
  const [refreshKey, setRefreshKey] = useState(0);
  const [notification, setNotification] = useState(null);

  // Load labels
  useEffect(() => {
    const url = new URL(`${API_URL}/api/status/labels`);
    url.searchParams.append("status", "pending");
    fetch(url.toString())
      .then((res) => res.json())
      .then((data) => setAvailableLabels(data.map((l) => ({ value: l.id, label: l.name }))))
      .catch((err) => console.error("Error loading labels:", err));
  }, []);

  // Load prompts
  useEffect(() => {
    const url = new URL(`${API_URL}/api/approve/prompts`);
    if (selectedLabel) url.searchParams.append("labels", selectedLabel.label);
    fetch(url.toString())
      .then((res) => res.json())
      .then((data) =>
        setAvailablePrompts(
          data.map((p) => ({
            value: p.id,
            label: p.content.length > 170 ? p.content.slice(0, 170) + "..." : p.content,
          }))
        )
      )
      .catch((err) => console.error("Error loading prompts:", err));
  }, [selectedLabel]);

  const filters = useMemo(() => ({
    labels: selectedLabel ? [selectedLabel.value] : [],
    prompts: selectedPrompt ? [selectedPrompt.value] : [],
  }), [selectedLabel, selectedPrompt]);

  const { images, imagesCount, loading } = usePreApproveImages({ page, limit, filters, refreshKey });

  const handleAction = async (action) => {
    const endpoint = action === "accept" ? "/api/preapprove/accept" : "/api/preapprove/reject";
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const email = user?.email || null;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selected, user_email: email, ids_with_shade: [] }),
      });
      if (!res.ok) throw new Error("Request failed");

      clear();
      setRefreshKey((prev) => prev + 1);
      setNotification({
        message: `${selected.length} images ${action === "accept" ? "pre-approved" : "pre-rejected"}`,
        type: action === "accept" ? "success" : "info",
      });
    } catch (err) {
      setNotification({ message: `Error: ${err.message}`, type: "error" });
    }
  };

  return (
    <div>
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <FiltersLabelPrompt
        availableLabels={availableLabels}
        selectedLabel={selectedLabel}
        setSelectedLabel={(v) => { setSelectedLabel(v); setPage(1); clear(); }}
        availablePrompts={availablePrompts}
        selectedPrompt={selectedPrompt}
        setSelectedPrompt={(v) => { setSelectedPrompt(v); setPage(1); clear(); }}
      />

      <select className="form-select mb-3" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); clear(); }}>
        <option value={100}>100</option>
        <option value={500}>500</option>
        <option value={1000}>1000</option>
        <option value={10000}>10000</option>
      </select>

      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-success" disabled={selected.length === 0} onClick={() => handleAction("accept")}>
          Approve Selected
        </button>
        <button className="btn btn-danger" disabled={selected.length === 0} onClick={() => handleAction("reject")}>
          Reject Selected
        </button>
      </div>

      <div className="container">
        <div className="row align-items-end">
          <h2 className="col-3">Media to Pre-Approve</h2>
          <div className="col-9" />
        </div>
      </div>

      <Pagination
        page={page}
        totalCount={imagesCount}
        limit={limit}
        loading={loading}
        onPrev={() => { setPage(page - 1); clear(); }}
        onNext={() => { setPage(page + 1); clear(); }}
      />

      <ImageTableView
        images={images}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
      />
    </div>
  );
}

export default PreApproveImages;
